import { FC } from 'react';
import { Loader, Page, PageTitleWithBack, Select, TextField } from '../../../../../components';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  getFieldWalkthroughManagementLocation,
  updateFieldWalkthroughManagementLocationItems
} from '../../../../../fetch/field-walkthrough-location-management';
import { Close, Save } from '@mui/icons-material';
import {
  Grid,
  Box,
  Button,
  FormControlLabel,
  Switch,
  MenuItem,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { ListManagementDetailsDragAndDrop } from './list-management-detail-drag-n-drop';
import { getFieldWalkthroughLocationManagementPlanDependencies } from '../../../../../fetch/lookups/field-walkthrough-location-management';
import { IFieldWalkthroughManagementLocationTemplateItem } from '../../../../../models';

const FieldWalkthroughLocation = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  planDependencyValue: Yup.mixed().when('showPlanDependencyValue', {
    is: val => val === true,
    then: Yup.number().typeError('Must be a number').required('Required'),
    otherwise: Yup.mixed().notRequired()
  }),
  templateItems: Yup.array()
    .of(
      Yup.object().shape({
        text: Yup.string().min(1, 'Text is required').required('Text is required')
      })
    )
    .required('Text is required')
});

interface IListManagementDetail {}

export const ListManagementDetail: FC<IListManagementDetail> = () => {
  const { fieldWalkthroughLocationId }: { fieldWalkthroughLocationId: string } = useParams();

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    isLoading: isLoadingFWTLocation,
    data: fwtLocation,
    refetch: refetchFwtLocation
  } = useQuery(
    ['fieldWalkthroughLocation', fieldWalkthroughLocationId],
    async () => await getFieldWalkthroughManagementLocation(fieldWalkthroughLocationId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!fieldWalkthroughLocationId
    }
  );

  const { isLoading: isLoadingPlanDependencies, data: planDependencyOptions } = useQuery(
    ['planDependencyOptions'],
    async () => await getFieldWalkthroughLocationManagementPlanDependencies(),
    {
      notifyOnChangeProps: 'tracked'
    }
  );

  return (
    <Page title={`${fwtLocation?.name ?? 'Field Walkthrough Location '}`}>
      {/* Should this live under production by itself? it's not linkable from Field Walkthroughs */}
      <PageTitleWithBack
        title={`${fwtLocation?.name ?? 'Field Walkthrough Location '}`}
        backText={'List Management'}
        backUrl={'/production/field-walkthroughs/list-management'}
      />
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: fwtLocation?.name ?? '',
          isActive: fwtLocation?.isActive ?? false,
          planDependencyType: fwtLocation?.planDependencyType ?? '',
          showPlanDependencyValue:
            planDependencyOptions?.find(option => option.value === fwtLocation?.planDependencyType)
              ?.inputValueRequired ?? false,
          planDependencyValue: fwtLocation?.planDependencyValue ?? '',
          templateItems: fwtLocation?.templateItems ?? []
        }}
        validationSchema={FieldWalkthroughLocation}
        onSubmit={async (values, actions) => {
          try {
            const data = {
              name: values?.name,
              isActive: values?.isActive,
              planDependencyType: values?.planDependencyType,
              planDependencyValue: planDependencyOptions?.find(
                option => option.value === values?.planDependencyType
              )?.inputValueRequired
                ? values?.planDependencyValue
                : null,
              templateItems: values?.templateItems?.map(
                (item: IFieldWalkthroughManagementLocationTemplateItem, index: number) => {
                  return {
                    templateItemId: item?.templateItemId?.toString()?.includes('new')
                      ? null
                      : item.templateItemId,
                    text: item?.text,
                    isActive: item?.isActive,
                    order: index + 1
                  };
                }
              )
            };
            await updateFieldWalkthroughManagementLocationItems(fieldWalkthroughLocationId, data);
            enqueueSnackbar(`Location updated successfully`, {
              variant: 'success'
            });
            await refetchFwtLocation();
            actions.resetForm();
            history.push('/production/field-walkthroughs/list-management');
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? `Error updating Location, please try again.`, {
              variant: 'error'
            });
          }
        }}
      >
        {({
          isSubmitting,
          values,
          handleChange,
          setFieldValue,
          handleSubmit,
          dirty,
          isValid,
          handleBlur,
          errors,
          touched
        }) => {
          return (
            <Form onSubmit={handleSubmit} autoComplete="none">
              {(isSubmitting || isLoadingFWTLocation) && (
                <Loader
                  type="fullscreen"
                  position="centered"
                  title={isSubmitting ? 'Saving...' : 'Loading...'}
                />
              )}
              <Grid container spacing={1}>
                <Grid item xs={10}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    label={'Name'}
                    name={'name'}
                    value={values.name}
                    InputLabelProps={{ shrink: !!values.name }}
                    required
                  />
                </Grid>
                <Grid item xs={2}>
                  <FormControlLabel
                    color="primary"
                    control={
                      <Switch
                        checked={values.isActive}
                        id="list-management-switch"
                        onChange={handleChange}
                        name={'isActive'}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={4}>
                  {!isLoadingPlanDependencies && !isLoadingFWTLocation && (
                    <Select
                      label="Plan Dependency Type"
                      name="planDependencyType"
                      value={values.planDependencyType}
                      onChange={event => {
                        let target = event.target as HTMLInputElement;
                        const { value } = target;
                        setFieldValue('planDependencyType', value);
                        const showPlanDependencyValue =
                          planDependencyOptions?.find(option => option.value === value)
                            ?.inputValueRequired ?? false;
                        setFieldValue('showPlanDependencyValue', showPlanDependencyValue);
                      }}
                      InputProps={{
                        endAdornment: !!values.planDependencyType && (
                          <InputAdornment
                            position="end"
                            sx={{
                              position: 'absolute',
                              right: 28,
                              cursor: 'pointer'
                            }}
                          >
                            <IconButton
                              disabled={isLoadingPlanDependencies || isLoadingFWTLocation}
                              sx={{ padding: 0 }}
                              title="Clear"
                              onClick={() => {
                                setFieldValue('planDependencyType', null);
                                setFieldValue('showPlanDependencyValue', false);
                              }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    >
                      {!isLoadingPlanDependencies &&
                        planDependencyOptions?.map(option => (
                          <MenuItem key={`${option?.value}`} value={option?.value}>
                            {option?.description}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                </Grid>
                {values.showPlanDependencyValue && (
                  <Grid item xs={4}>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      label={'Plan Dependency Value'}
                      name={'planDependencyValue'}
                      required
                    />
                  </Grid>
                )}
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <ListManagementDetailsDragAndDrop
                    setFieldValue={setFieldValue}
                    values={values.templateItems}
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    handleSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    dirty={dirty}
                    isValid={isValid}
                  />
                </Grid>
              </Grid>
              <Box marginTop="1rem">
                <Button
                  disabled={!dirty || isSubmitting || !isValid}
                  type="submit"
                  startIcon={<Save />}
                  variant="contained"
                  color="primary"
                >
                  Update
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Page>
  );
};
